import { useEffect, useMemo, useRef, useState } from 'react'
import styles from './Raid.module.css'

export type EnrageTier = 'PRIMARY' | 'AMBER' | 'CRIMSON' | 'ENRAGED'

const HOUR_MS = 3_600_000
const CRIMSON_THRESHOLD_MS = 6 * HOUR_MS
const AMBER_THRESHOLD_MS = 24 * HOUR_MS

const classifyRemaining = (remainingMs: number): EnrageTier => {
  if (remainingMs <= 0) return 'ENRAGED'
  if (remainingMs < CRIMSON_THRESHOLD_MS) return 'CRIMSON'
  if (remainingMs < AMBER_THRESHOLD_MS) return 'AMBER'
  return 'PRIMARY'
}

const formatCountdown = (remainingMs: number): string => {
  if (remainingMs <= 0) return 'ENRAGED'
  const totalSeconds = Math.floor(remainingMs / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (value: number) => value.toString().padStart(2, '0')
  if (hours >= 24) {
    const days = Math.floor(hours / 24)
    const remHours = hours % 24
    return `${days}d ${pad(remHours)}h ${pad(minutes)}m`
  }
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

interface EnrageTimerProps {
  deadlineIso: string
  onTierChange?: (tier: EnrageTier) => void
}

/**
 * Countdown pill that escalates color as the enrage deadline approaches.
 * Cadence: 60s while >= 6h remaining, 1s below. Freezes at 00:00:00 → "ENRAGED".
 * Pulse animation on the crimson band respects prefers-reduced-motion via CSS.
 */
export const EnrageTimer = ({ deadlineIso, onTierChange }: EnrageTimerProps) => {
  const deadlineMs = useMemo(() => Date.parse(deadlineIso), [deadlineIso])
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const tick = () => setNow(Date.now())
    const scheduleNext = (): number => {
      const remaining = deadlineMs - Date.now()
      const interval = remaining <= 0 || remaining < CRIMSON_THRESHOLD_MS ? 1000 : 60_000
      return window.setTimeout(() => {
        tick()
        timeoutRef.current = scheduleNext()
      }, interval)
    }
    const timeoutRef: { current: number } = { current: scheduleNext() }
    return () => window.clearTimeout(timeoutRef.current)
  }, [deadlineMs])

  const remainingMs = deadlineMs - now
  const tier = classifyRemaining(remainingMs)
  const label = tier === 'ENRAGED' ? 'Enrage Timer' : 'Enrage In'
  const value = formatCountdown(remainingMs)

  const previousTierRef = useRef<EnrageTier | null>(null)
  useEffect(() => {
    if (previousTierRef.current !== tier) {
      previousTierRef.current = tier
      onTierChange?.(tier)
    }
  }, [tier, onTierChange])

  const tierClass =
    tier === 'ENRAGED'
      ? styles.timerEnraged
      : tier === 'CRIMSON'
        ? styles.timerCrimson
        : tier === 'AMBER'
          ? styles.timerAmber
          : ''

  return (
    <div className={`${styles.timer} ${tierClass}`} role="timer" aria-live="polite">
      <span className={styles.timerLabel}>{label}</span>
      <span className={styles.timerValue}>{value}</span>
    </div>
  )
}
